import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

/**
 * Minimal GDPR-style cookie-consent banner for Airon.
 * Styled with Airon design tokens (ink background, airon accent).
 *
 * Persists the user's choice to localStorage under `airon_cookie_consent`.
 * Valid values: "accepted" | "rejected".
 *
 * Emits a custom "airon:consent" window event so any analytics/tracking
 * bootstrap code can listen and only initialize once the user accepts.
 */

const STORAGE_KEY = "airon_cookie_consent";

function writeConsent(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
    window.dispatchEvent(
      new CustomEvent("airon:consent", { detail: { value } })
    );
  } catch (_) {
    // ignore quota / privacy-mode errors
  }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        // Delay slightly so banner doesn't flash before the hero renders.
        const t = setTimeout(() => setVisible(true), 400);
        return () => clearTimeout(t);
      }
    } catch (_) {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const accept = () => {
    writeConsent("accepted");
    setVisible(false);
  };
  const reject = () => {
    writeConsent("rejected");
    setVisible(false);
  };

  return (
    <div
      role="region"
      aria-label="Informasjonskapsler"
      style={{
        position: "fixed",
        bottom: 20,
        left: 20,
        right: 20,
        zIndex: 2000,
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          pointerEvents: "auto",
          width: "100%",
          maxWidth: 960,
          background: "var(--ink)",
          color: "#fff",
          borderRadius: 20,
          padding: "20px 24px",
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 20,
          alignItems: "center",
          boxShadow: "0 20px 60px rgba(14,34,51,0.35)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: "var(--mono)",
              fontSize: 11,
              color: "var(--airon)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              marginBottom: 6,
            }}
          >
            Informasjonskapsler
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 14,
              lineHeight: 1.55,
              color: "#D6E0EA",
            }}
          >
            Vi bruker kun informasjonskapsler som er nødvendige for at
            nettsiden skal fungere, samt valgfri statistikk for å forbedre
            opplevelsen. Du kan lese mer i vår{" "}
            <Link
              to="/side/personvern"
              style={{
                color: "#fff",
                borderBottom: "1px solid rgba(255,255,255,0.3)",
              }}
            >
              personvernerklæring
            </Link>
            .
          </p>
        </div>
        <div
          className="cookie-consent-actions"
          style={{ display: "flex", gap: 10, flexShrink: 0 }}
        >
          <button
            type="button"
            onClick={reject}
            className="btn ghost"
            style={{
              color: "#fff",
              borderColor: "rgba(255,255,255,0.25)",
              background: "transparent",
            }}
          >
            Kun nødvendige
          </button>
          <button type="button" onClick={accept} className="btn">
            Godta alle <span className="arr">→</span>
          </button>
        </div>
      </div>
      <style>{`
        @media (max-width: 640px) {
          [aria-label="Informasjonskapsler"] > div {
            grid-template-columns: 1fr !important;
          }
          [aria-label="Informasjonskapsler"] .cookie-consent-actions {
            flex-wrap: wrap;
          }
          [aria-label="Informasjonskapsler"] .cookie-consent-actions .btn {
            flex: 1;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}
